import React, { Fragment, useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { SITENAMEALIAS, WEBSERVICE } from "../utils/init";
import { fetchEscrowContractDetails, getAuthToken, getAuthUuid } from "../utils/service";
import { showToast } from "../utils/library";


// ─────────────────────────────────────────────────────────────────
// CSS
// ─────────────────────────────────────────────────────────────────
const CSS = `
.ecd-page { padding: 32px 24px 80px; max-width: 1000px; overflow-x: hidden; }

.ecd-back-link { display: inline-flex; align-items: center; gap: 7px; height: 34px; padding: 0 14px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; margin-bottom: 22px; user-select: none; transition: all .15s; text-decoration: none; font-family: inherit; background: var(--body-color-less); border: 1px solid var(--body-color-less2); color: var(--text-color); }
.ecd-back-link:hover { border-color: var(--btn-color); color: var(--btn-color); background: color-mix(in srgb, var(--btn-color) 6%, transparent); }

.ecd-hdr { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 8px; gap: 16px; flex-wrap: wrap; }
.ecd-hdr-left { display: flex; flex-direction: column; gap: 6px; }
.ecd-title { font-size: 22px; font-weight: 700; letter-spacing: -.4px; color: var(--text-color); margin: 0; }
.ecd-id { font-size: 12px; font-weight: 600; color: var(--text-color); opacity: .5; letter-spacing: .4px; }
.ecd-hdr-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }

/* ── Badges ── */
.ecd-badge { display: inline-flex; align-items: center; gap: 4px; padding: 4px 11px; border-radius: 20px; font-size: 12px; font-weight: 600; }
.ecd-badge::before { content: ''; width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
.ecd-badge.funded    { background: rgba(16,185,129,.15); color: #10b981; }
.ecd-badge.funded::before    { background: #10b981; }
.ecd-badge.unfunded  { background: rgba(100,116,139,.15); color: #94a3b8; }
.ecd-badge.unfunded::before  { background: #94a3b8; }
.ecd-badge.in_dispute { background: rgba(217,119,6,.13); color: #d97706; }
.ecd-badge.in_dispute::before { background: #d97706; }
.ecd-badge.disputed  { background: rgba(239,68,68,.1); color: #f87171; }
.ecd-badge.disputed::before  { background: #f87171; }
.ecd-badge.in_review { background: rgba(139,92,246,.12); color: #a78bfa; }
.ecd-badge.in_review::before { background: #a78bfa; }
.ecd-badge.release_approved { background: rgba(16,185,129,.15); color: #10b981; }
.ecd-badge.release_approved::before { background: #10b981; }
.ecd-badge.released  { background: rgba(37,99,235,.12); color: #60a5fa; }
.ecd-badge.released::before  { background: #60a5fa; }
.ecd-badge.cancelled { background: rgba(239,68,68,.1); color: #f87171; }
.ecd-badge.cancelled::before { background: #f87171; }
.ecd-badge.fund-initiated { background: rgba(245,158,11,.12); color: #f59e0b; }
.ecd-badge.fund-initiated::before { background: #f59e0b; }
.ecd-badge.partially-funded { background: rgba(234,88,12,.12); color: #ea580c; }
.ecd-badge.partially-funded::before { background: #ea580c; }
.ecd-badge.funding-rejected { background: rgba(239,68,68,.1); color: #f87171; }
.ecd-badge.funding-rejected::before { background: #f87171; }
.ecd-badge.created { background: rgba(37,99,235,.12); color: #60a5fa; }
.ecd-badge.created::before { background: #60a5fa; }

.ecd-role-chip { display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .6px; border: 1px solid; }
.ecd-role-chip.payer    { background: color-mix(in srgb, var(--btn-color) 10%, transparent); border-color: color-mix(in srgb, var(--btn-color) 28%, transparent); color: var(--btn-color); }
.ecd-role-chip.payee    { background: rgba(16,185,129,.1); border-color: rgba(16,185,129,.28); color: #10b981; }
.ecd-role-chip.approval { background: rgba(217,119,6,.1); border-color: rgba(217,119,6,.28); color: #d97706; }

/* ── Tabs ── */
.ecd-tab-bar { display: flex; gap: 0; border-bottom: 1px solid var(--body-color-less2); margin-bottom: 22px; margin-top: 20px; overflow-x: auto; }
.ecd-tab { height: 40px; padding: 0 18px; background: none; border: none; border-bottom: 2px solid transparent; font-family: inherit; font-size: 13px; font-weight: 600; color: var(--text-color); opacity: .55; cursor: pointer; transition: all .15s; white-space: nowrap; display: inline-flex; align-items: center; gap: 7px; margin-bottom: -1px; }
.ecd-tab:hover { opacity: .85; }
.ecd-tab.active { color: var(--btn-color); opacity: 1; border-bottom-color: var(--btn-color); }
.ecd-tab-pane { animation: ecd-fade-up .18s ease; }

/* ── Sections ── */
.ecd-section { background: var(--body-color-less); border: 1px solid var(--body-color-less2); border-radius: 12px; padding: 22px; margin-bottom: 18px; }
.ecd-section-title { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .8px; color: var(--text-color); opacity: .65; margin: 0 0 18px; padding-bottom: 11px; border-bottom: 1px solid var(--body-color-less2); display: flex; align-items: center; gap: 8px; }

/* ── Meta grid ── */
.ecd-meta-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.ecd-meta-item { display: flex; flex-direction: column; gap: 4px; }
.ecd-meta-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; color: var(--text-color); opacity: .5; }
.ecd-meta-value { font-size: 14px; font-weight: 600; color: var(--text-color); }
.ecd-meta-value.amount { font-size: 22px; font-weight: 700; color: var(--btn-color); }
.ecd-meta-value.mono { font-family: monospace; font-size: 12px; word-break: break-all; }

.ecd-desc { font-size: 14px; color: var(--text-color); opacity: .8; line-height: 1.65; margin: 0; }

/* ── Alerts ── */
.ecd-alert { display: flex; align-items: flex-start; gap: 12px; padding: 14px 16px; border-radius: 10px; margin-bottom: 16px; font-size: 13px; line-height: 1.55; }
.ecd-alert.warning { background: rgba(217,119,6,.1); border: 1px solid rgba(217,119,6,.25); color: #d97706; }
.ecd-alert.info    { background: rgba(37,99,235,.08); border: 1px solid rgba(37,99,235,.2); color: #60a5fa; }
.ecd-alert.success { background: rgba(16,185,129,.08); border: 1px solid rgba(16,185,129,.22); color: #10b981; }
.ecd-alert.error   { background: rgba(239,68,68,.08); border: 1px solid rgba(239,68,68,.22); color: #f87171; }

/* ── Action buttons ── */
.ecd-btn { height: 36px; padding: 0 16px; border-radius: 8px; font-family: inherit; font-size: 13px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; transition: all .15s; white-space: nowrap; }
.ecd-btn-fund    { background: rgba(5,150,105,.12); border: 1px solid rgba(5,150,105,.3); color: #059669; }
.ecd-btn-fund:hover    { background: rgba(5,150,105,.22); }
.ecd-btn-dispute { background: rgba(217,119,6,.1); border: 1px solid rgba(217,119,6,.28); color: #d97706; }
.ecd-btn-dispute:hover { background: rgba(217,119,6,.2); }
.ecd-btn-cancel  { background: rgba(239,68,68,.08); border: 1px solid rgba(239,68,68,.25); color: #f87171; }
.ecd-btn-cancel:hover  { background: rgba(239,68,68,.18); }
.ecd-btn-release { background: rgba(16,185,129,.12); border: 1px solid rgba(16,185,129,.3); color: #10b981; }
.ecd-btn-release:hover { background: rgba(16,185,129,.22); }
.ecd-btn-reject  { background: rgba(239,68,68,.08); border: 1px solid rgba(239,68,68,.25); color: #f87171; }
.ecd-btn-reject:hover  { background: rgba(239,68,68,.18); }
.ecd-btn-approve { background: rgba(16,185,129,.12); border: 1px solid rgba(16,185,129,.3); color: #10b981; }
.ecd-btn-approve:hover { background: rgba(16,185,129,.22); }
.ecd-btn-approve:disabled { opacity: .45; cursor: not-allowed; pointer-events: none; }
.ecd-btn-reject:disabled  { opacity: .45; cursor: not-allowed; pointer-events: none; }
.ecd-btn-dispute:disabled { opacity: .45; cursor: not-allowed; pointer-events: none; }

/* ── Milestone Dispute Modal ── */
.ecd-ms-dispute-bg { position: fixed; inset: 0; background: rgba(0,0,0,.65); backdrop-filter: blur(4px); z-index: 1000; display: flex; align-items: center; justify-content: center; opacity: 0; pointer-events: none; transition: opacity .2s; }
.ecd-ms-dispute-bg.open { opacity: 1; pointer-events: all; }
.ecd-ms-dispute-box { background: var(--body-color-less); border: 1px solid var(--body-color-less2); border-radius: 14px; padding: 24px; width: 440px; max-width: 92vw; transform: translateY(12px); transition: transform .2s; }
.ecd-ms-dispute-bg.open .ecd-ms-dispute-box { transform: translateY(0); }
.ecd-ms-dispute-title { font-size: 16px; font-weight: 700; color: var(--text-color); margin: 0 0 4px; }
.ecd-ms-dispute-sub { font-size: 12px; color: var(--text-color); opacity: .6; margin: 0 0 18px; }
.ecd-ms-dispute-label { display: block; font-size: 12px; font-weight: 600; color: var(--text-color); opacity: .75; margin-bottom: 5px; }
.ecd-ms-dispute-textarea { width: 100%; min-height: 90px; background: var(--body-color-less); border: 1.5px solid var(--body-color-less2); border-radius: 8px; padding: 10px 12px; color: var(--text-color); font-family: inherit; font-size: 13px; resize: vertical; outline: none; box-sizing: border-box; transition: border-color .2s; }
.ecd-ms-dispute-textarea:focus { border-color: var(--btn-color); }
.ecd-ms-dispute-file-label { display: flex; align-items: center; gap: 8px; height: 38px; padding: 0 14px; border: 1.5px dashed var(--body-color-less2); border-radius: 8px; cursor: pointer; font-size: 12px; color: var(--text-color); opacity: .7; transition: all .15s; margin-top: 14px; }
.ecd-ms-dispute-file-label:hover { border-color: var(--btn-color); opacity: 1; color: var(--btn-color); }
.ecd-ms-dispute-file-label-error { border-color: rgba(239,68,68,.55) !important; color: #f87171 !important; opacity: 1 !important; }
.ecd-ms-dispute-file-name { font-size: 11px; color: var(--text-color); opacity: .6; margin-top: 5px; }
.ecd-ms-dispute-file-error { font-size: 11px; color: #f87171; margin-top: 5px; display: flex; align-items: center; gap: 4px; }
.ecd-ms-dispute-file-hint { font-size: 11px; color: var(--text-color); opacity: .45; margin-top: 5px; }
.ecd-ms-dispute-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 20px; }
.ecd-ms-dispute-cancel-btn { height: 36px; padding: 0 18px; border-radius: 8px; border: 1px solid var(--body-color-less2); background: transparent; color: var(--text-color); opacity: .8; font-family: inherit; font-size: 13px; font-weight: 500; cursor: pointer; transition: all .15s; }
.ecd-ms-dispute-cancel-btn:hover { opacity: 1; border-color: var(--text-color); }
.ecd-ms-dispute-submit-btn { height: 36px; padding: 0 18px; border-radius: 8px; border: none; background: #d97706; color: #fff; font-family: inherit; font-size: 13px; font-weight: 600; cursor: pointer; transition: filter .15s; display: inline-flex; align-items: center; gap: 7px; }
.ecd-ms-dispute-submit-btn:hover:not(:disabled) { filter: brightness(1.1); }
.ecd-ms-dispute-submit-btn:disabled { opacity: .55; cursor: not-allowed; }
.ecd-btn-view-approvals { background: color-mix(in srgb, var(--btn-color) 10%, transparent); border: 1px solid color-mix(in srgb, var(--btn-color) 28%, transparent); color: var(--btn-color); }
.ecd-btn-view-approvals:hover { background: color-mix(in srgb, var(--btn-color) 20%, transparent); }
.ecd-btn-request { background: rgba(37,99,235,.1); border: 1px solid rgba(37,99,235,.28); color: #60a5fa; }
.ecd-btn-request:hover { background: rgba(37,99,235,.2); }
.ecd-btn-respond { background: rgba(217,119,6,.1); border: 1px solid rgba(217,119,6,.28); color: #d97706; }
.ecd-btn-respond:hover { background: rgba(217,119,6,.2); }
/* ── Approve Terms Modal ── */
.ecd-approve-terms-bg { position: fixed; inset: 0; background: rgba(0,0,0,.65); backdrop-filter: blur(4px); z-index: 1000; display: flex; align-items: center; justify-content: center; opacity: 0; pointer-events: none; transition: opacity .2s; }
.ecd-approve-terms-bg.open { opacity: 1; pointer-events: all; }
.ecd-approve-terms-box { background: var(--body-color-less); border: 1px solid var(--body-color-less2); border-radius: 14px; padding: 28px; width: 420px; max-width: 92vw; transform: translateY(12px); transition: transform .2s; }
.ecd-approve-terms-bg.open .ecd-approve-terms-box { transform: translateY(0); }
.ecd-approve-terms-icon { width: 48px; height: 48px; border-radius: 50%; background: rgba(16,185,129,.12); border: 1.5px solid rgba(16,185,129,.3); display: flex; align-items: center; justify-content: center; font-size: 20px; color: #10b981; margin-bottom: 16px; }
.ecd-approve-terms-title { font-size: 17px; font-weight: 700; color: var(--text-color); margin: 0 0 6px; }
.ecd-approve-terms-sub { font-size: 12.5px; color: var(--text-color); opacity: .6; margin: 0 0 20px; line-height: 1.55; }
.ecd-approve-terms-check { display: flex; align-items: flex-start; gap: 10px; margin: 0 0 22px; }
.ecd-approve-terms-check input[type="checkbox"] { margin-top: 2px; accent-color: #10b981; width: 15px; height: 15px; flex-shrink: 0; cursor: pointer; }
.ecd-approve-terms-check label { font-size: 12.5px; color: var(--text-color); opacity: .8; line-height: 1.55; cursor: pointer; }
.ecd-approve-terms-check label a { color: #10b981; text-decoration: underline; }
.ecd-approve-terms-actions { display: flex; justify-content: flex-end; gap: 10px; }
.ecd-approve-terms-cancel { height: 38px; padding: 0 18px; border-radius: 8px; border: 1px solid var(--body-color-less2); background: transparent; color: var(--text-color); opacity: .8; font-family: inherit; font-size: 13px; font-weight: 500; cursor: pointer; transition: all .15s; }
.ecd-approve-terms-cancel:hover { opacity: 1; border-color: var(--text-color); }
.ecd-approve-terms-proceed { height: 38px; padding: 0 22px; border-radius: 8px; border: none; background: #10b981; color: #fff; font-family: inherit; font-size: 13px; font-weight: 700; cursor: pointer; box-shadow: 0 0 12px rgba(16,185,129,.3); transition: filter .15s; display: inline-flex; align-items: center; gap: 7px; }
.ecd-approve-terms-proceed:hover:not(:disabled) { filter: brightness(1.08); }
.ecd-approve-terms-proceed:disabled { opacity: .45; cursor: not-allowed; box-shadow: none; filter: none; }

/* ── Participants ── */
.ecd-participant-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.ecd-participant-card { background: var(--body-color); border: 1px solid var(--body-color-less2); border-radius: 10px; padding: 18px; }
.ecd-participant-role { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .6px; color: var(--text-color); opacity: .5; margin-bottom: 10px; }
.ecd-participant-name { font-size: 15px; font-weight: 700; color: var(--text-color); margin-bottom: 4px; }
.ecd-participant-email { font-size: 12px; color: var(--text-color); opacity: .65; margin-bottom: 8px; }
.ecd-participant-wallet { font-family: monospace; font-size: 11px; color: var(--text-color); opacity: .55; background: var(--body-color-less); border: 1px solid var(--body-color-less2); border-radius: 6px; padding: 5px 9px; display: inline-block; word-break: break-all; }

/* ── Approvers list ── */
.ecd-approvers-list { display: flex; flex-direction: column; gap: 0; }
.ecd-approver-row { display: flex; align-items: center; justify-content: space-between; padding: 13px 0; border-bottom: 1px solid var(--body-color-less2); }
.ecd-approver-row:last-child { border-bottom: none; }
.ecd-approver-info { display: flex; flex-direction: column; gap: 2px; }
.ecd-approver-role-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; color: var(--text-color); opacity: .5; }
.ecd-approver-name { font-size: 13px; font-weight: 600; color: var(--text-color); }
.ecd-approval-pill { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 700; }
.ecd-approval-pill.approved { background: rgba(16,185,129,.14); color: #10b981; }
.ecd-approval-pill.pending  { background: rgba(100,116,139,.14); color: #94a3b8; }
.ecd-approval-pill.rejected { background: rgba(239,68,68,.12); color: #f87171; }
.ecd-approval-pill::before  { content: ''; width: 5px; height: 5px; border-radius: 50%; background: currentColor; flex-shrink: 0; }

/* ── Conditions ── */
.ecd-conditions-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 10px; }
.ecd-condition-item { display: flex; align-items: flex-start; gap: 10px; font-size: 13px; color: var(--text-color); opacity: .8; line-height: 1.55; }
.ecd-condition-num { min-width: 22px; height: 22px; border-radius: 50%; background: color-mix(in srgb, var(--btn-color) 12%, transparent); color: var(--btn-color); font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px; }
.ecd-terms-box { font-size: 13px; color: var(--text-color); opacity: .75; line-height: 1.65; margin: 0; padding: 14px 16px; background: var(--body-color); border: 1px solid var(--body-color-less2); border-radius: 8px; }
.ecd-terms-box ul, .ecd-terms-box ol { margin: 6px 0 6px 20px; padding: 0; }
.ecd-terms-box li { margin-bottom: 4px; }
.ecd-terms-box p { margin: 0 0 8px; }
.ecd-terms-box b, .ecd-terms-box strong { font-weight: 700; }
.ecd-terms-box i, .ecd-terms-box em { font-style: italic; }
.ecd-terms-box u { text-decoration: underline; }

/* ── Distribution ── */
.ecd-dist-total { font-size: 28px; font-weight: 700; color: var(--btn-color); margin-bottom: 4px; }
.ecd-dist-total-label { font-size: 12px; color: var(--text-color); opacity: .5; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 20px; }
.ecd-dist-list { display: flex; flex-direction: column; gap: 14px; }
.ecd-dist-item { display: flex; flex-direction: column; gap: 6px; }
.ecd-dist-item-hdr { display: flex; justify-content: space-between; align-items: center; }
.ecd-dist-label { font-size: 13px; font-weight: 600; color: var(--text-color); }
.ecd-dist-amount { font-size: 13px; font-weight: 700; color: var(--text-color); }
.ecd-dist-pct { font-size: 11px; color: var(--text-color); opacity: .5; margin-left: 6px; }
.ecd-dist-bar-track { height: 6px; background: var(--body-color-less2); border-radius: 99px; overflow: hidden; }
.ecd-dist-bar-fill { height: 100%; border-radius: 99px; background: var(--btn-color); transition: width .4s ease; }
.ecd-dist-bar-fill.completed  { background: #10b981; }
.ecd-dist-bar-fill.in_progress { background: var(--btn-color); }
.ecd-dist-bar-fill.disputed   { background: #d97706; }
.ecd-dist-bar-fill.cancelled  { background: #f87171; }

/* ── Milestone badge (distribution tab) ── */
.ecd-ms-badge { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 12px; font-size: 11px; font-weight: 600; }
.ecd-ms-badge.completed  { background: rgba(16,185,129,.13); color: #10b981; }
.ecd-ms-badge.approved   { background: rgba(16,185,129,.13); color: #10b981; }
.ecd-ms-badge.success    { background: rgba(16,185,129,.18); color: #059669; }
.ecd-ms-badge.in_progress { background: rgba(37,99,235,.12); color: #60a5fa; }
.ecd-ms-badge.pending    { background: rgba(100,116,139,.13); color: #94a3b8; }
.ecd-ms-badge.disputed   { background: rgba(217,119,6,.13); color: #d97706; }
.ecd-ms-badge.cancelled  { background: rgba(239,68,68,.1); color: #f87171; }

/* ── Activity Log ── */
.ecd-activity-list { display: flex; flex-direction: column; gap: 0; position: relative; }
.ecd-activity-item { display: flex; gap: 14px; padding: 12px 0; position: relative; }
.ecd-activity-item:not(:last-child)::after { content: ''; position: absolute; left: 10px; top: 32px; bottom: -12px; width: 1px; background: var(--body-color-less2); }
.ecd-activity-dot { width: 21px; height: 21px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; margin-top: 2px; font-size: 9px; position: relative; z-index: 1; }
.ecd-activity-dot.create  { background: color-mix(in srgb, var(--btn-color) 15%, transparent); color: var(--btn-color); border: 1.5px solid color-mix(in srgb, var(--btn-color) 35%, transparent); }
.ecd-activity-dot.fund    { background: rgba(16,185,129,.15); color: #10b981; border: 1.5px solid rgba(16,185,129,.35); }
.ecd-activity-dot.submit  { background: rgba(37,99,235,.12); color: #60a5fa; border: 1.5px solid rgba(37,99,235,.3); }
.ecd-activity-dot.dispute { background: rgba(217,119,6,.13); color: #d97706; border: 1.5px solid rgba(217,119,6,.3); }
.ecd-activity-dot.approve { background: rgba(16,185,129,.13); color: #10b981; border: 1.5px solid rgba(16,185,129,.3); }
.ecd-activity-dot.release { background: rgba(16,185,129,.15); color: #10b981; border: 1.5px solid rgba(16,185,129,.35); }
.ecd-activity-dot.cancel  { background: rgba(239,68,68,.1); color: #f87171; border: 1.5px solid rgba(239,68,68,.25); }
.ecd-activity-body { flex: 1; }
.ecd-activity-action { font-size: 13px; font-weight: 500; color: var(--text-color); line-height: 1.45; }
.ecd-activity-meta { font-size: 11px; color: var(--text-color); opacity: .5; margin-top: 3px; }

/* ── Confirm dialog ── */
.ecd-confirm-bg { position: fixed; inset: 0; background: rgba(0,0,0,.65); backdrop-filter: blur(4px); z-index: 1000; display: flex; align-items: center; justify-content: center; opacity: 0; pointer-events: none; transition: opacity .2s; }
.ecd-confirm-bg.open { opacity: 1; pointer-events: all; }
.ecd-confirm-box { background: var(--body-color-less); border: 1px solid var(--body-color-less2); border-radius: 14px; padding: 24px; width: 360px; max-width: 90vw; transform: translateY(12px); transition: transform .2s; }
.ecd-confirm-bg.open .ecd-confirm-box { transform: translateY(0); }
.ecd-confirm-title { font-size: 16px; font-weight: 700; color: var(--text-color); margin: 0 0 8px; }
.ecd-confirm-msg { font-size: 13px; color: var(--text-color); opacity: .7; margin: 0 0 20px; line-height: 1.55; }
.ecd-confirm-actions { display: flex; justify-content: flex-end; gap: 10px; }
.ecd-confirm-cancel-btn { height: 36px; padding: 0 18px; border-radius: 8px; border: 1px solid var(--body-color-less2); background: transparent; color: var(--text-color); opacity: .8; font-family: inherit; font-size: 13px; font-weight: 500; cursor: pointer; transition: all .15s; }
.ecd-confirm-cancel-btn:hover { opacity: 1; border-color: var(--text-color); }
.ecd-confirm-ok-btn { height: 36px; padding: 0 18px; border-radius: 8px; border: none; background: var(--btn-color); color: #fff; font-family: inherit; font-size: 13px; font-weight: 600; cursor: pointer; transition: filter .15s; }
.ecd-confirm-ok-btn:hover { filter: brightness(1.1); }
.ecd-confirm-ok-btn.danger { background: #ef4444; }

/* ── Not found ── */
.ecd-not-found { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 60vh; gap: 14px; text-align: center; }
.ecd-not-found-icon { font-size: 48px; opacity: .25; color: var(--text-color); }
.ecd-not-found-title { font-size: 20px; font-weight: 700; color: var(--text-color); margin: 0; }
.ecd-not-found-sub { font-size: 14px; color: var(--text-color); opacity: .55; margin: 0; }

@keyframes ecd-fade-up { from { opacity: 0; transform: translateY(7px); } to { opacity: 1; transform: translateY(0); } }
.ecd-view-in { animation: ecd-fade-up .2s ease; }

@media (max-width: 700px) {
  .ecd-page { padding: 16px 10px 80px; }
  .ecd-meta-grid { grid-template-columns: 1fr 1fr; }
  .ecd-participant-grid { grid-template-columns: 1fr; }
  .ecd-hdr { flex-direction: column; }
  .ecd-tab { padding: 0 12px; font-size: 12px; }
}
@media (max-width: 460px) {
  .ecd-meta-grid { grid-template-columns: 1fr; }
}
`;

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────
const STATUS_LABELS = { funded: "Funded", unfunded: "Unfunded", created: "Created", in_dispute: "In Dispute", disputed: "Disputed", in_review: "In Review", release_approved: "Release Approved", released: "Released", completed: "Completed", cancelled: "Cancelled" };
const MS_STATUS_LABELS = { completed: "Completed", approved: "Approved", success: "Success", in_progress: "In Progress", pending: "Pending", disputed: "Disputed", cancelled: "Cancelled" };
const ROLE_LABELS = { payer: "Payer", payee: "Payee", approval: "Approver" };
const ROLE_ICONS  = { payer: "fa-user", payee: "fa-handshake", approval: "fa-gavel" };

const ACTIVITY_ICONS = { create: "fa-plus", fund: "fa-wallet", submit: "fa-paper-plane", dispute: "fa-exclamation-triangle", approve: "fa-check", release: "fa-unlock", cancel: "fa-times" };

const EVENT_TYPE_META = {
  CONTRACT_CREATED:     { type: "create",  label: "Contract Created" },
  CONTRACT_FUNDED:      { type: "fund",    label: "Contract Funded" },
  CONTRACT_ACTIVE:      { type: "fund",    label: "Contract Activated" },
  CONTRACT_APPROVED:    { type: "approve", label: "Contract Approved" },
  RELEASE_APPROVED:     { type: "approve", label: "Release Approved" },
  FUNDS_RELEASED:       { type: "release", label: "Funds Released" },
  CONTRACT_RELEASED:    { type: "release", label: "Contract Released" },
  COMPLETED:            { type: "release", label: "Contract Completed" },
  DISPUTE_RAISED:       { type: "dispute", label: "Dispute Raised" },
  DISPUTE_RESOLVED:     { type: "approve", label: "Dispute Resolved" },
  CONTRACT_CANCELLED:   { type: "cancel",  label: "Contract Cancelled" },
  CANCELLED:            { type: "cancel",  label: "Contract Cancelled" },
  APPROVAL_SUBMITTED:   { type: "submit",  label: "Approval Submitted" },
  MILESTONE_APPROVED:   { type: "approve", label: "Milestone Approved" },
  MILESTONE_COMPLETED:  { type: "approve", label: "Milestone Completed" },
};

function eventTypeMeta(eventType) {
  if (!eventType) return { type: "submit", label: "Event" };
  const key = (eventType || "").toUpperCase();
  if (EVENT_TYPE_META[key]) return EVENT_TYPE_META[key];
  // Fallback: prettify snake_case → Title Case
  const label = key.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  return { type: "submit", label };
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
function fmtDateTime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

// ─────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const label = (STATUS_LABELS[status] || status || "").toUpperCase().replace(/_/g, " ");
  return <span className={`ecd-badge ${status}`}>{label}</span>;
}

const FUNDING_STATUS_LABELS = {
  FUNDED:            "Funded",
  FUND_INITIATED:    "Fund Initiated",
  PENDING:           "Pending",
  CANCELLED:         "Cancelled",
  RELEASED:          "Released",
  PARTIALLY_FUNDED:  "Partially Funded",
  FUNDING_REJECTED:  "Fund Rejected",
};
const FUNDING_STATUS_CSS = {
  FUNDED:            "funded",
  FUND_INITIATED:    "fund-initiated",
  PENDING:           "unfunded",
  CANCELLED:         "cancelled",
  RELEASED:          "released",
  PARTIALLY_FUNDED:  "partially-funded",
  FUNDING_REJECTED:  "funding-rejected",
};
function FundingStatusBadge({ status }) {
  if (!status) return null;
  const key = (status || "").toUpperCase();
  const cls   = FUNDING_STATUS_CSS[key]   || key.toLowerCase().replace(/_/g, "-");
  const label = (FUNDING_STATUS_LABELS[key] || key).toUpperCase().replace(/_/g, " ");
  return <span className={`ecd-badge ${cls}`}>{label}</span>;
}
function MsBadge({ status }) {
  const key = (status || "").toLowerCase();
  return <span className={`ecd-ms-badge ${key}`}>{MS_STATUS_LABELS[key] || status}</span>;
}
function ApprovalPill({ status }) {
  return <span className={`ecd-approval-pill ${status}`}>{status.charAt(0).toUpperCase() + status.slice(1)}</span>;
}

function ConfirmDialog({ open, title, message, confirmLabel, danger, onConfirm, onCancel }) {
  return (
    <div className={`ecd-confirm-bg${open ? " open" : ""}`}>
      <div className="ecd-confirm-box">
        <p className="ecd-confirm-title">{title}</p>
        <p className="ecd-confirm-msg">{message}</p>
        <div className="ecd-confirm-actions">
          <button className="ecd-confirm-cancel-btn" onClick={onCancel}>Cancel</button>
          <button className={`ecd-confirm-ok-btn${danger ? " danger" : ""}`} onClick={onConfirm}>
            {confirmLabel || "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RejectConfirmDialog({ open, milestoneId, trackingId, comment, onCommentChange, onConfirm, onCancel, loading }) {
  return (
    <div className={`ecd-confirm-bg${open ? " open" : ""}`}>
      <div className="ecd-confirm-box" style={{ width: 420 }}>
        <p className="ecd-confirm-title">Confirm Rejection</p>
        <p className="ecd-confirm-msg">Please provide a reason for rejecting this item.</p>
        <textarea
          value={comment}
          onChange={(e) => onCommentChange(e.target.value)}
          placeholder="Enter rejection reason..."
          rows={4}
          style={{
            width: "100%",
            boxSizing: "border-box",
            resize: "vertical",
            padding: "10px 12px",
            borderRadius: 8,
            border: "1px solid var(--body-color-less2)",
            background: "var(--body-color-less)",
            color: "var(--text-color)",
            fontSize: 13,
            fontFamily: "inherit",
            marginBottom: 18,
            outline: "none",
          }}
        />
        <div className="ecd-confirm-actions">
          <button className="ecd-confirm-cancel-btn" onClick={onCancel} disabled={loading}>Cancel</button>
          <button
            className="ecd-confirm-ok-btn danger"
            onClick={onConfirm}
            disabled={loading || !comment.trim()}
          >
            {loading ? <><i className="fas fa-spinner fa-spin" style={{ fontSize: 12 }} /> Rejecting…</> : "Confirm Rejection"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Tab panels
// ─────────────────────────────────────────────────────────────────
function TabOverview({ c, role }) {
  return (
    <div className="ecd-tab-pane">
      {/* Status alerts */}
      {c.status === "in_dispute" && c.disputeReason && (
        <div className="ecd-alert warning">
          <i className="fas fa-exclamation-triangle" style={{ marginTop: 1 }} />
          <div><strong>Dispute Active — </strong>{c.disputeReason}</div>
        </div>
      )}
      {c.status === "cancelled" && (
        <div className="ecd-alert error">
          <i className="fas fa-ban" style={{ marginTop: 1 }} />
          <div>
            <strong>Contract Cancelled</strong>
            {c.cancelReason ? ` — ${c.cancelReason}` : ""}
            {c.cancelledAt ? ` on ${fmtDate(c.cancelledAt)}` : ""}
          </div>
        </div>
      )}
      {c.status === "released" && (
        <div className="ecd-alert success">
          <i className="fas fa-check-circle" style={{ marginTop: 1 }} />
          <div><strong>Funds Released</strong>{c.releasedAt ? ` — Escrow settled on ${fmtDateTime(c.releasedAt)}` : ""}</div>
        </div>
      )}

      {/* Key metrics */}
      <div className="ecd-section">
        <p className="ecd-section-title">Contract Summary</p>
        <div className="ecd-meta-grid">
          <div className="ecd-meta-item">
            <span className="ecd-meta-label">Total Amount</span>
            <span className="ecd-meta-value amount">{c.currency ? `${c.currency} ` : ""}{c.amount}</span>
          </div>
          <div className="ecd-meta-item">
            <span className="ecd-meta-label">Contract Status</span>
            <span className="ecd-meta-value"><StatusBadge status={c.status} /></span>
          </div>
          <div className="ecd-meta-item">
            <span className="ecd-meta-label">Funding Status</span>
            <span className="ecd-meta-value">
              {c.fundingStatus
                ? <FundingStatusBadge status={c.fundingStatus} />
                : <span style={{ opacity: 0.35 }}>—</span>}
            </span>
          </div>
          <div className="ecd-meta-item">
            <span className="ecd-meta-label">Created</span>
            <span className="ecd-meta-value">{fmtDateTime(c.createdAt)}</span>
          </div>
          <div className="ecd-meta-item">
            <span className="ecd-meta-label">Funded</span>
            <span className="ecd-meta-value">{fmtDateTime(c.fundedAt)}</span>
          </div>
          <div className="ecd-meta-item">
            <span className="ecd-meta-label">Expires</span>
            <span className="ecd-meta-value">{fmtDate(c.expiresAt)}</span>
          </div>
          {c.transactionHash && (
            <div className="ecd-meta-item">
              <span className="ecd-meta-label">Tx Hash</span>
              <span className="ecd-meta-value mono">{c.transactionHash}</span>
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      <div className="ecd-section">
        <p className="ecd-section-title">Description</p>
        <p className="ecd-desc">{c.description}</p>
      </div>

      {/* Approval Status — approval role only */}
      {role === "approval" && c.approvalStatus && (
        <div className="ecd-section">
          <p className="ecd-section-title">
            <i className="fas fa-gavel" style={{ fontSize: 11 }} /> Approval Status
          </p>
          <div className="ecd-approvers-list">
            {c.approvalStatus.map((a, i) => (
              <div className="ecd-approver-row" key={i}>
                <div className="ecd-approver-info">
                  <span className="ecd-approver-role-label">{a.role}</span>
                  <span className="ecd-approver-name">{a.name}</span>
                </div>
                <ApprovalPill status={a.status} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TabParticipants({ c, role }) {
  const payers = c.payers && c.payers.length > 0
    ? c.payers
    : [{ name: c.payer, email: c.payerEmail, walletAddress: c.payerWallet }];
  const payees = c.payees && c.payees.length > 0
    ? c.payees
    : [{ name: c.payee, email: c.payeeEmail, walletAddress: c.payeeWallet }];

  return (
    <div className="ecd-tab-pane">
      {/* Payers */}
      <div className="ecd-section" style={{ marginBottom: 18 }}>
        <p className="ecd-section-title" style={{ marginBottom: 10 }}>
          <i className="fas fa-user" style={{ fontSize: 11 }} /> Payers
        </p>
        <div className="ecd-participant-grid">
          {payers.map((p, i) => (
            <div className="ecd-participant-card" key={p.participantId || i}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <div className="ecd-participant-role" style={{ margin: 0 }}>Payer</div>
                {p.isFunded === "YES" && (
                  <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 20, background: "rgba(16,185,129,.13)", color: "#10b981", border: "1px solid rgba(16,185,129,.28)" }}>
                    Funded
                  </span>
                )}
                {p.isFunded === "NO" && (
                  <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 20, background: "rgba(100,116,139,.13)", color: "#94a3b8", border: "1px solid rgba(100,116,139,.28)" }}>
                    Pending
                  </span>
                )}
              </div>
              <div className="ecd-participant-name">{p.name || p.email || "—"}</div>
              <div className="ecd-participant-email">{p.name ? p.email : ""}</div>
              {p.walletAddress && <span className="ecd-participant-wallet">{p.walletAddress}</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Payees */}
      <div className="ecd-section" style={{ marginBottom: 18 }}>
        <p className="ecd-section-title" style={{ marginBottom: 10 }}>
          <i className="fas fa-handshake" style={{ fontSize: 11 }} /> Payees
        </p>
        <div className="ecd-participant-grid">
          {payees.map((p, i) => (
            <div className="ecd-participant-card" key={p.participantId || i}>
              <div className="ecd-participant-role">Payee</div>
              <div className="ecd-participant-name">{p.name || p.email || "—"}</div>
              <div className="ecd-participant-email">{p.name ? p.email : ""}</div>
              {p.walletAddress && <span className="ecd-participant-wallet">{p.walletAddress}</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Approvers — shown for approval role */}
      {role === "approval" && c.approvalStatus && (
        <div className="ecd-section">
          <p className="ecd-section-title">
            <i className="fas fa-gavel" style={{ fontSize: 11 }} /> Approvers
          </p>
          <div className="ecd-approvers-list">
            {c.approvalStatus.map((a, i) => (
              <div className="ecd-approver-row" key={i}>
                <div className="ecd-approver-info">
                  <span className="ecd-approver-role-label">{a.role}</span>
                  <span className="ecd-approver-name">{a.name}</span>
                </div>
                <ApprovalPill status={a.status} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Milestone Dispute Modal
// ─────────────────────────────────────────────────────────────────
const DISPUTE_ALLOWED_TYPES = ["image/jpeg", "image/png", "application/pdf"];
const DISPUTE_ALLOWED_EXT   = ".jpg, .jpeg, .png, .pdf";
const DISPUTE_MAX_MB        = 5;
const DISPUTE_MAX_B         = DISPUTE_MAX_MB * 1024 * 1024;

function MilestoneDisputeModal({ open, contractNumericId, milestoneId, raisedBy, milestoneLabel, onClose, onSuccess }) {
  const [description, setDescription] = useState("");
  const [file, setFile]               = useState(null);
  const [fileError, setFileError]     = useState("");
  const [submitting, setSubmitting]   = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) { setDescription(""); setFile(null); setFileError(""); setSubmitting(false); }
  }, [open]);

  const handleFileChange = (e) => {
    const selected = e.target.files[0] || null;
    if (!selected) { setFile(null); setFileError(""); return; }
    if (!DISPUTE_ALLOWED_TYPES.includes(selected.type)) {
      setFile(null);
      setFileError(`Invalid file type. Allowed: ${DISPUTE_ALLOWED_EXT}`);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    if (selected.size > DISPUTE_MAX_B) {
      setFile(null);
      setFileError(`File exceeds ${DISPUTE_MAX_MB} MB limit.`);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setFile(selected);
    setFileError("");
  };

  const handleSubmit = async () => {
    if (!description.trim()) { showToast("error", "Please enter a description."); return; }
    if (fileError) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("contractId",   contractNumericId);
      fd.append("raisedBy",     raisedBy);
      fd.append("description",  description.trim());
      fd.append("milestoneId",  milestoneId ?? "");
      if (file) fd.append("evidenceFile", file);

      const res = await fetch(`${WEBSERVICE}/escrow/raise`, {
        method: "POST",
        headers: {
          Authorization: "bearer " + getAuthToken(),
          UUID: getAuthUuid(),
        },
        body: fd,
      });
      const data = await res.json();
      if (data.status === true) {
        showToast("success", data.message || "Dispute raised successfully.");
        onSuccess();
        onClose();
      } else {
        showToast("error", data.message || "Failed to raise dispute.");
      }
    } catch {
      showToast("error", "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`ecd-ms-dispute-bg${open ? " open" : ""}`} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ecd-ms-dispute-box">
        <p className="ecd-ms-dispute-title">
          <i className="fas fa-exclamation-triangle" style={{ marginRight: 8, color: "#d97706" }} />
          Open Dispute
        </p>
        <p className="ecd-ms-dispute-sub">{milestoneLabel ? `Milestone: ${milestoneLabel}` : ""}</p>

        <label className="ecd-ms-dispute-label">
          Description <span style={{ color: "#f87171" }}>*</span>
        </label>
        <textarea
          className="ecd-ms-dispute-textarea"
          placeholder="Describe the issue in detail…"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={submitting}
        />

        <label className="ecd-ms-dispute-label" style={{ marginTop: 14 }}>
          Evidence File <span style={{ opacity: .5 }}>(optional)</span>
        </label>
        <label className={`ecd-ms-dispute-file-label${fileError ? " ecd-ms-dispute-file-label-error" : ""}`}>
          <i className="fas fa-paperclip" style={{ fontSize: 12 }} />
          {file ? file.name : "Attach a file…"}
          <input
            ref={fileRef}
            type="file"
            accept=".jpg,.jpeg,.png,.pdf"
            style={{ display: "none" }}
            onChange={handleFileChange}
            disabled={submitting}
          />
        </label>
        {fileError ? (
          <div className="ecd-ms-dispute-file-error">
            <i className="fas fa-exclamation-circle" style={{ fontSize: 11 }} /> {fileError}
          </div>
        ) : file ? (
          <div className="ecd-ms-dispute-file-name">
            {file.name}
            <span
              style={{ marginLeft: 8, cursor: "pointer", color: "#f87171" }}
              onClick={() => { setFile(null); setFileError(""); if (fileRef.current) fileRef.current.value = ""; }}
            >
              <i className="fas fa-times" />
            </span>
          </div>
        ) : (
          <div className="ecd-ms-dispute-file-hint">
            Accepted: {DISPUTE_ALLOWED_EXT} &nbsp;·&nbsp; Max {DISPUTE_MAX_MB} MB
          </div>
        )}

        <div className="ecd-ms-dispute-actions">
          <button className="ecd-ms-dispute-cancel-btn" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="ecd-ms-dispute-submit-btn" onClick={handleSubmit} disabled={submitting}>
            {submitting
              ? <><i className="fas fa-spinner fa-spin" style={{ fontSize: 11 }} /> Submitting…</>
              : <><i className="fas fa-exclamation-triangle" style={{ fontSize: 11 }} /> Open Dispute</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

function TabConditions({ c, role, myEmail, myMerchantId, approveLoadingId, onMilestoneApprove, onMilestoneDispute, rejectLoadingId, onMilestoneReject }) {
  const isMilestone = c.releaseType === "MILESTONE_BASED";
  const escrowDocuments = c.escrowDocuments || [];
  const approversList = c.approvalStatus || [];
  const myEmailLower = (myEmail || "").toLowerCase();
  const [approveModal, setApproveModal] = useState(null); // { milestoneId, conditionId }
  const [approveTermsAccepted, setApproveTermsAccepted] = useState(false);

  return (
    <div className="ecd-tab-pane">
      <div className="ecd-section">
        <p className="ecd-section-title">
          {isMilestone ? "Milestones" : "Contract Conditions"}
        </p>

        {/* Release config summary */}
        {isMilestone && (c.approvalModel || c.approvalThreshold > 1) && (
          <div style={{
            display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 14,
            padding: "10px 14px", background: "color-mix(in srgb, var(--btn-color) 6%, transparent)",
            border: "1px solid color-mix(in srgb, var(--btn-color) 18%, transparent)", borderRadius: 8,
          }}>
            {c.approvalModel && (
              <span style={{ fontSize: 12, color: "var(--text-color)", opacity: 0.7, display: "flex", alignItems: "center", gap: 6 }}>
                <i className="fas fa-gavel" style={{ fontSize: 11, color: "var(--btn-color)" }} />
                <strong>Approval Model:</strong>&nbsp;{c.approvalModel}
              </span>
            )}
            {c.approvalThreshold > 1 && (
              <span style={{ fontSize: 12, color: "var(--text-color)", opacity: 0.7, display: "flex", alignItems: "center", gap: 6 }}>
                <i className="fas fa-check-double" style={{ fontSize: 11, color: "var(--btn-color)" }} />
                <strong>Threshold:</strong>&nbsp;{c.approvalThreshold} approvals required
              </span>
            )}
          </div>
        )}

        {isMilestone ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {(c.milestones || []).map((ms, i) => {
              // Determine if logged-in approver is assigned to this milestone.
              // If assignedApproverIndexes is empty, fall back to any contract approver.
              const isAssignedApprover = role === "approval" && (
                ms.assignedApproverIndexes.length === 0
                  ? approversList.some((a) => (a.email || "").toLowerCase() === myEmailLower)
                  : ms.assignedApproverIndexes.some((idx) => {
                      const a = approversList[idx];
                      return a && (a.email || "").toLowerCase() === myEmailLower;
                    })
              );
              const isFunded = (c.status === "funded" || (c.fundingStatus || "").toUpperCase() === "FUNDED");
              const canApprove = isAssignedApprover &&
                ["pending", "in_progress"].includes(ms.status) &&
                isFunded &&
                c.status !== "in_dispute" && c.status !== "disputed";
              const isApproved = isAssignedApprover && ms.status === "approved";
              const canDispute = (role === "payer" || role === "payee") &&
                (c.status === "funded" || (c.fundingStatus || "").toUpperCase() === "FUNDED") &&
                c.status !== "in_dispute" && c.status !== "disputed" && c.status !== "cancelled";
              const isApproving = approveLoadingId === ms.milestoneId;
              const isRejecting = rejectLoadingId === ms.milestoneId;

              return (
                <div
                  key={i}
                  style={{
                    background: "var(--body-color)",
                    border: "1px solid var(--body-color-less2)",
                    borderRadius: 10,
                    padding: "14px 16px",
                  }}
                >
                  {/* ── Milestone header ── */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{
                        width: 22, height: 22, borderRadius: "50%",
                        background: "color-mix(in srgb, var(--btn-color) 15%, transparent)",
                        color: "var(--btn-color)", fontSize: 11, fontWeight: 700,
                        display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}>{i + 1}</span>
                      <span style={{ fontWeight: 700, fontSize: 14, color: "var(--text-color)" }}>{ms.label}</span>
                      <MsBadge status={ms.status} />
                    </div>
                    {/* pct + amount */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "var(--btn-color)" }}>{ms.pct}%</span>
                      {ms.amount && (
                        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-color)", opacity: 0.55 }}>
                          {c.currency ? `${c.currency} ` : ""}{ms.amount}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Description / deliverables */}
                  {ms.description && (
                    <div style={{ fontSize: 12, color: "var(--text-color)", opacity: 0.65, marginLeft: 32, marginBottom: 4 }}>
                      {ms.description}
                    </div>
                  )}

                  {/* Meta row: due date, trigger type */}
                  <div style={{ display: "flex", gap: 16, marginLeft: 32, marginTop: 4, flexWrap: "wrap" }}>
                    {ms.completionDate && (
                      <span style={{ fontSize: 11, color: "var(--text-color)", opacity: 0.55 }}>
                        <i className="fas fa-calendar-alt" style={{ marginRight: 4 }} />
                        Due: {new Date(ms.completionDate).toLocaleDateString()}
                      </span>
                    )}
                    {ms.triggerType && (
                      <span style={{ fontSize: 11, color: "var(--text-color)", opacity: 0.55 }}>
                        <i className="fas fa-bolt" style={{ marginRight: 4 }} />
                        Release: {ms.triggerType === "APPROVAL" ? "On Approval" : "On Date"}
                      </span>
                    )}
                  </div>

                  {/* Assigned approver chips */}
                  {ms.assignedApproverIndexes.length > 0 && (
                    <div style={{ marginLeft: 32, marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                      <span style={{ fontSize: 11, color: "var(--text-color)", opacity: 0.5, marginRight: 2 }}>
                        <i className="fas fa-user-check" style={{ marginRight: 4 }} />Approvers:
                      </span>
                      {ms.assignedApproverIndexes.map((idx) => {
                        const a = approversList[idx];
                        if (!a) return null;
                        return (
                          <span
                            key={idx}
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 4,
                              padding: "3px 10px", borderRadius: 8, fontSize: 11, fontWeight: 500,
                              background: "color-mix(in srgb, var(--btn-color) 10%, transparent)",
                              color: "var(--btn-color)",
                              border: "1px solid color-mix(in srgb, var(--btn-color) 20%, transparent)",
                            }}
                          >
                            <i className="fas fa-user" style={{ fontSize: 9 }} />
                            <span>{a.name}{a.role ? ` · ${a.role}` : ""}</span>
                            {a.email && (
                              <span style={{ opacity: 0.65, fontWeight: 400 }}>{a.email}</span>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {/* Attached documents */}
                  {(() => {
                    const msDocs = escrowDocuments.filter((d) => d.milestoneId === ms.milestoneId);
                    if (!msDocs.length) return null;
                    return (
                      <div style={{ marginLeft: 32, marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {msDocs.map((doc, di) => (
                          <a
                            key={di}
                            href={doc.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 5,
                              padding: "3px 10px", borderRadius: 8, fontSize: 11, fontWeight: 500,
                              background: "color-mix(in srgb, var(--btn-color) 10%, transparent)",
                              color: "var(--btn-color)", textDecoration: "none",
                              border: "1px solid color-mix(in srgb, var(--btn-color) 25%, transparent)",
                            }}
                          >
                            <i className="fas fa-paperclip" style={{ fontSize: 10 }} />
                            {doc.fileName}
                            <i className="fas fa-external-link-alt" style={{ fontSize: 9, opacity: 0.7 }} />
                          </a>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Action row — only for assigned approvers on pending/in-progress milestones */}
                  {canApprove && (
                    <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--body-color-less2)", display: "flex", justifyContent: "flex-end", gap: 10 }}>
                      <button
                        className="ecd-btn ecd-btn-dispute"
                        disabled={isApproving}
                        onClick={() => onMilestoneDispute(ms.milestoneId, ms.label)}
                      >
                        <i className="fas fa-exclamation-triangle" style={{ fontSize: 12 }} /> Open Dispute
                      </button>
                      <button
                        className="ecd-btn ecd-btn-approve"
                        disabled={isApproving || isRejecting}
                        onClick={() => { setApproveModal({ milestoneId: ms.milestoneId, conditionId: null }); setApproveTermsAccepted(false); }}
                      >
                        {isApproving ? (
                          <><i className="fas fa-spinner fa-spin" style={{ fontSize: 12 }} /> Approving…</>
                        ) : (
                          <><i className="fas fa-check-circle" style={{ fontSize: 12 }} /> Approve Milestone</>
                        )}
                      </button>
                      <button
                        className="ecd-btn ecd-btn-reject"
                        disabled={isRejecting || isApproving}
                        onClick={() => onMilestoneReject(ms.milestoneId)}
                      >
                        {isRejecting ? (
                          <><i className="fas fa-spinner fa-spin" style={{ fontSize: 12 }} /> Rejecting…</>
                        ) : (
                          <><i className="fas fa-times-circle" style={{ fontSize: 12 }} /> Reject Milestone</>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Approved label — for approver when milestone is already approved */}
                  {isApproved && (
                    <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--body-color-less2)", display: "flex", justifyContent: "flex-end" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#10b981" }}>
                        <i className="fas fa-check-circle" style={{ fontSize: 13 }} /> Approved
                      </span>
                    </div>
                  )}

                  {/* Open Dispute — always visible for payer / payee when funded */}
                  {canDispute && (
                    <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--body-color-less2)", display: "flex", justifyContent: "flex-end" }}>
                      <button
                        className="ecd-btn ecd-btn-dispute"
                        onClick={() => onMilestoneDispute(ms.milestoneId, ms.label)}
                      >
                        <i className="fas fa-exclamation-triangle" style={{ fontSize: 12 }} /> Open Dispute
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {(c.conditions || []).map((cond, i) => {
              // Support legacy string conditions (mock data)
              if (typeof cond === "string") {
                return (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 13, color: "var(--text-color)", opacity: 0.8, lineHeight: 1.55 }}>
                    <span className="ecd-condition-num">{i + 1}</span>
                    {cond}
                  </div>
                );
              }

              // Rich condition object from API
              const condStatusMap = {
                PENDING:   "pending",
                APPROVED:  "completed",
                COMPLETED: "completed",
                SUCCESS:   "success",
                ACTIVE:    "in_progress",
                CANCELLED: "cancelled",
                REJECTED:  "cancelled",
              };
              const displayStatus = condStatusMap[cond.status] || "pending";

              // Approve button: any contract-level approver whose email matches logged-in user
              const isContractApprover = role === "approval" &&
                approversList.some((a) => (a.email || "").toLowerCase() === myEmailLower);
              const isContractFunded = (c.status === "funded" || (c.fundingStatus || "").toUpperCase() === "FUNDED")
                && c.status !== "release_approved";
              const canApprove = isContractApprover && cond.status === "PENDING" && isContractFunded &&
                c.status !== "in_dispute" && c.status !== "disputed";
              const canDispute = (role === "payer" || role === "payee") &&
                (c.status === "funded" || (c.fundingStatus || "").toUpperCase() === "FUNDED") &&
                c.status !== "in_dispute" && c.status !== "disputed" && c.status !== "cancelled";
              const isApproving = approveLoadingId === cond.conditionId;
              const isRejecting = rejectLoadingId === cond.conditionId;

              return (
                <div
                  key={cond.conditionId || i}
                  style={{
                    background: "var(--body-color)",
                    border: "1px solid var(--body-color-less2)",
                    borderRadius: 10,
                    padding: "14px 16px",
                  }}
                >
                  {/* Header */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{
                        width: 22, height: 22, borderRadius: "50%",
                        background: "color-mix(in srgb, var(--btn-color) 15%, transparent)",
                        color: "var(--btn-color)", fontSize: 11, fontWeight: 700,
                        display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}>{cond.sequenceNo || i + 1}</span>
                      <span style={{ fontWeight: 700, fontSize: 14, color: "var(--text-color)" }}>
                        {cond.label || `Condition ${i + 1}`}
                      </span>
                      <MsBadge status={displayStatus} />
                    </div>
                    {/* Release pct / amount */}
                    {(cond.releasePercentage != null || cond.releaseAmount > 0) && (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                        {cond.releasePercentage != null && (
                          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--btn-color)" }}>
                            {cond.releasePercentage}%
                          </span>
                        )}
                        {cond.releaseAmount > 0 && (
                          <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-color)", opacity: 0.55 }}>
                            {c.currency ? `${c.currency} ` : ""}{Number(cond.releaseAmount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  {cond.description && cond.description !== cond.label && (
                    <div style={{ fontSize: 12, color: "var(--text-color)", opacity: 0.65, marginLeft: 32, marginBottom: 4 }}>
                      {cond.description}
                    </div>
                  )}

                  {/* Meta: trigger + release date */}
                  <div style={{ display: "flex", gap: 16, marginLeft: 32, marginTop: 4, flexWrap: "wrap" }}>
                    {cond.releaseTrigger && (
                      <span style={{ fontSize: 11, color: "var(--text-color)", opacity: 0.55 }}>
                        <i className="fas fa-bolt" style={{ marginRight: 4 }} />
                        Release: {cond.releaseTrigger === "APPROVAL" ? "On Approval" : cond.releaseTrigger === "DATE" ? "On Date" : cond.releaseTrigger}
                      </span>
                    )}
                    {cond.conditionType && (
                      <span style={{ fontSize: 11, color: "var(--text-color)", opacity: 0.55 }}>
                        <i className="fas fa-tag" style={{ marginRight: 4 }} />
                        {cond.conditionType.replace(/_/g, " ")}
                      </span>
                    )}
                    {cond.releaseDate && (
                      <span style={{ fontSize: 11, color: "var(--text-color)", opacity: 0.55 }}>
                        <i className="fas fa-calendar-alt" style={{ marginRight: 4 }} />
                        On: {new Date(cond.releaseDate).toLocaleDateString()}
                      </span>
                    )}
                  </div>

                  {/* Approver chips — all contract-level approvers */}
                  {approversList.length > 0 && (
                    <div style={{ marginLeft: 32, marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                      <span style={{ fontSize: 11, color: "var(--text-color)", opacity: 0.5, marginRight: 2 }}>
                        <i className="fas fa-user-check" style={{ marginRight: 4 }} />Approvers:
                      </span>
                      {approversList.map((a, ai) => (
                        <span
                          key={ai}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 4,
                            padding: "3px 10px", borderRadius: 8, fontSize: 11, fontWeight: 500,
                            background: "color-mix(in srgb, var(--btn-color) 10%, transparent)",
                            color: "var(--btn-color)",
                            border: "1px solid color-mix(in srgb, var(--btn-color) 20%, transparent)",
                          }}
                        >
                          <i className="fas fa-user" style={{ fontSize: 9 }} />
                          {a.name && <span>{a.name}{a.role ? ` · ${a.role}` : ""}</span>}
                          {a.email && (
                            <span style={{ opacity: 0.65, fontWeight: 400 }}>{a.email}</span>
                          )}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Approve / Reject buttons */}
                  {canApprove && (
                    <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--body-color-less2)", display: "flex", justifyContent: "flex-end", gap: 10 }}>
                      <button
                        className="ecd-btn ecd-btn-approve"
                        disabled={isApproving || isRejecting}
                        onClick={() => { setApproveModal({ milestoneId: null, conditionId: cond.conditionId }); setApproveTermsAccepted(false); }}
                      >
                        {isApproving ? (
                          <><i className="fas fa-spinner fa-spin" style={{ fontSize: 12 }} /> Approving…</>
                        ) : (
                          <><i className="fas fa-check-circle" style={{ fontSize: 12 }} /> Approve</>
                        )}
                      </button>
                      <button
                        className="ecd-btn ecd-btn-reject"
                        disabled={isRejecting || isApproving}
                        onClick={() => onMilestoneReject(null, cond.conditionId)}
                      >
                        {isRejecting ? (
                          <><i className="fas fa-spinner fa-spin" style={{ fontSize: 12 }} /> Rejecting…</>
                        ) : (
                          <><i className="fas fa-times-circle" style={{ fontSize: 12 }} /> Reject</>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Open Dispute — always visible for payer / payee when funded */}
                  {canDispute && (
                    <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--body-color-less2)", display: "flex", justifyContent: "flex-end" }}>
                      <button
                        className="ecd-btn ecd-btn-dispute"
                        onClick={() => onMilestoneDispute(null, cond.label || cond.title || "")}
                      >
                        <i className="fas fa-exclamation-triangle" style={{ fontSize: 12 }} /> Open Dispute
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="ecd-section">
        <p className="ecd-section-title">Terms &amp; Conditions</p>
        {c.terms
          ? <div className="ecd-terms-box" dangerouslySetInnerHTML={{ __html: c.terms }} />
          : <p className="ecd-terms-box" style={{ opacity: 0.45 }}>No terms and conditions provided.</p>}
      </div>

      {/* Approve Terms Modal */}
      <div className={`ecd-approve-terms-bg${approveModal ? " open" : ""}`}>
        <div className="ecd-approve-terms-box">
          <div className="ecd-approve-terms-icon">
            <i className="fas fa-check-circle" />
          </div>
          <p className="ecd-approve-terms-title">Confirm Approval</p>
          <p className="ecd-approve-terms-sub">Please review and accept the terms before proceeding with your approval.</p>
          <div className="ecd-approve-terms-check">
            <input
              type="checkbox"
              id="ecd-approve-terms-cb"
              checked={approveTermsAccepted}
              onChange={e => setApproveTermsAccepted(e.target.checked)}
            />
            <label htmlFor="ecd-approve-terms-cb">
              I agree to the{" "}
              <a href="https://www.paybito.com/escrow/approver-terms" target="_blank" rel="noopener noreferrer">Approver Terms</a>{" "}
              and understand my approval authority.
            </label>
          </div>
          <div className="ecd-approve-terms-actions">
            <button
              className="ecd-approve-terms-cancel"
              onClick={() => { setApproveModal(null); setApproveTermsAccepted(false); }}
            >
              Cancel
            </button>
            <button
              className="ecd-approve-terms-proceed"
              disabled={!approveTermsAccepted}
              onClick={() => {
                const { milestoneId, conditionId } = approveModal;
                setApproveModal(null);
                setApproveTermsAccepted(false);
                onMilestoneApprove(milestoneId, conditionId);
              }}
            >
              <i className="fas fa-check-circle" style={{ fontSize: 13 }} /> Proceed
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TabDistribution({ c }) {
  const payers = c.payers && c.payers.length > 0
    ? c.payers
    : (c.payer && c.payer !== "—" ? [{ name: c.payer, email: c.payerEmail, sharePercent: 100 }] : []);

  return (
    <div className="ecd-tab-pane">
      {/* Payers contribution */}
      {payers.length > 0 && (
        <div className="ecd-section">
          <p className="ecd-section-title">Payers</p>
          <div className="ecd-dist-list">
            {payers.map((p, i) => {
              const pct = p.sharePercent != null ? Number(p.sharePercent) : 0;
              const label = p.name || p.email || "—";
              const sub   = p.name ? p.email : null;
              return (
                <div className="ecd-dist-item" key={p.participantId || i}>
                  <div className="ecd-dist-item-hdr">
                    <div>
                      <span className="ecd-dist-label">{label}</span>
                      {sub && (
                        <div style={{ fontSize: 11, color: "var(--text-color)", opacity: 0.5, marginTop: 2 }}>
                          {sub}
                        </div>
                      )}
                    </div>
                    <span className="ecd-dist-pct" style={{ fontSize: 13, fontWeight: 700, opacity: 1 }}>
                      {pct}%
                    </span>
                  </div>
                  <div className="ecd-dist-bar-track">
                    <div className="ecd-dist-bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Milestone distribution */}
      <div className="ecd-section">
        <p className="ecd-section-title">Fund Distribution</p>
        <div className="ecd-dist-total">{c.currency ? `${c.currency} ` : ""}{c.amount}</div>
        <div className="ecd-dist-total-label">Total Escrow Amount</div>
        <div className="ecd-dist-list">
          {(c.milestones || []).map((ms, i) => (
            <div className="ecd-dist-item" key={i}>
              <div className="ecd-dist-item-hdr">
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span className="ecd-dist-label">{ms.label}</span>
                  <MsBadge status={ms.status} />
                </div>
                <div>
                  <span className="ecd-dist-amount">{c.currency ? `${c.currency} ` : ""}{ms.amount}</span>
                  <span className="ecd-dist-pct">{ms.pct}%</span>
                </div>
              </div>
              <div className="ecd-dist-bar-track">
                <div
                  className={`ecd-dist-bar-fill ${ms.status}`}
                  style={{ width: `${ms.pct}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TabActivityLog({ c }) {
  const raw = c.activityLog || [];
  const log = [...raw].sort((a, b) => new Date(b.eventTimestamp) - new Date(a.eventTimestamp));

  return (
    <div className="ecd-tab-pane">
      <div className="ecd-section">
        <p className="ecd-section-title">
          <i className="fas fa-history" style={{ fontSize: 11 }} /> Activity Log
        </p>
        {log.length === 0 ? (
          <p style={{ fontSize: 13, color: "var(--text-color)", opacity: .5, margin: 0 }}>No activity recorded yet.</p>
        ) : (
          <div className="ecd-activity-list">
            {log.map((entry, i) => {
              const { type, label } = eventTypeMeta(entry.eventType);
              const icon = ACTIVITY_ICONS[type] || "fa-circle";
              return (
                <div className="ecd-activity-item" key={i}>
                  <div className={`ecd-activity-dot ${type}`}>
                    <i className={`fas ${icon}`} />
                  </div>
                  <div className="ecd-activity-body">
                    <div className="ecd-activity-action">{label}</div>
                    <div className="ecd-activity-meta">
                      {entry.eventTimestamp ? fmtDateTime(entry.eventTimestamp) : "—"}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────
const ROLE_LABELS_FULL = { payer: "Payer", payee: "Payee", approval: "Approver" };
const ROLE_ICONS_FULL  = { payer: "fa-user", payee: "fa-handshake", approval: "fa-gavel" };

const TABS = [
  { key: "overview",     label: "Overview",     icon: "fa-file-alt" },
  { key: "participants", label: "Participants",  icon: "fa-users" },
  { key: "conditions",   label: "Conditions",   icon: "fa-list-ul" },
  { key: "distribution", label: "Distribution", icon: "fa-chart-pie" },
  { key: "activity",     label: "Activity Log", icon: "fa-history" },
];

const CONFIRM_CONFIGS = {
  fund:            { title: "Fund Escrow",            confirmLabel: "Fund Escrow",         danger: false },
  dispute:         { title: "Open Dispute",           confirmLabel: "Open Dispute",        danger: false },
  cancel:          { title: "Cancel Contract",        confirmLabel: "Cancel Contract",     danger: true  },
  request_approval:{ title: "Request Approval",       confirmLabel: "Submit Request",      danger: false },
  respond_dispute: { title: "Respond to Dispute",     confirmLabel: "Submit Response",     danger: false },
  approve_release: { title: "Approve Release",        confirmLabel: "Approve Release",     danger: false },
  reject_release:  { title: "Reject Release",         confirmLabel: "Reject Release",      danger: true  },
};

const CONFIRM_MESSAGES = {
  fund:             (c) => `You are about to deposit ${c.currency ? c.currency + " " : ""}${c.amount} into escrow. This action cannot be undone once the payee begins work.`,
  dispute:          ()  => "Opening a dispute will freeze the escrow funds and notify the resolution team. Are you sure?",
  cancel:           ()  => "Are you sure you want to cancel this contract? Since escrow is unfunded, no funds will be affected.",
  request_approval: ()  => "Submit an approval request? All designated approvers will be notified to review the contract.",
  respond_dispute:  ()  => "You are about to submit your response to the active dispute. Make sure all evidence has been attached before proceeding.",
  approve_release:  (c) => `Approving will release ${c.currency ? c.currency + " " : ""}${c.amount} to the payee. This action is irreversible.`,
  reject_release:   ()  => "Rejecting will return the escrowed funds to the payer. The payee will be notified of this decision.",
};

// ── Status mapping from API values to display values ──────────────
const API_STATUS_MAP = {
  SAVE_DRAFT:       "unfunded",
  CREATED:          "created",
  ACTIVE:           "funded",
  IN_DISPUTE:       "in_dispute",
  DISPUTED:         "disputed",
  IN_REVIEW:        "in_review",
  RELEASE_APPROVED: "release_approved",
  COMPLETED:        "completed",
  CANCELLED:        "cancelled",
  REJECTED:         "cancelled",
  FUNDED:           "funded",
  RELEASED:         "released",
};

const MILESTONE_STATUS_MAP = {
  SAVE_DRAFT: "pending",
  PENDING:    "pending",
  ACTIVE:     "in_progress",
  APPROVED:   "approved",
  COMPLETED:  "completed",
  CANCELLED:  "cancelled",
  RELEASED:   "completed",
};

function mapApiContract(raw) {
  const participants = raw.participants || [];
  const payerList    = participants.filter((p) => p.role === "PAYER");
  const payeeList    = participants.filter((p) => p.role === "PAYEE");
  const approvers    = participants.filter((p) => p.role === "APPROVER");

  const fmtAmt = (n) =>
    Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return {
    id:             raw.contractNo,
    numericId:      raw.contractId,
    title:          raw.contractTitle || "—",
    amount:         fmtAmt(raw.totalContractAmount || raw.fundedAmount || 0),
    fundedAmount:   fmtAmt(raw.fundedAmount),
    currency:       raw.fundedCurrency || "",
    // Keep arrays for multi-payer/payee; also expose first for legacy single-value fields
    payers:         payerList,
    payees:         payeeList,
    payer:          payerList[0]?.name  || "—",
    payerEmail:     payerList[0]?.email || "",
    payee:          payeeList[0]?.name  || "—",
    payeeEmail:     payeeList[0]?.email || "",
    status:         API_STATUS_MAP[raw.status] || (raw.status || "").toLowerCase(),
    fundingStatus:  raw.fundingStatus || "",
    releaseType:    raw.releaseType    || "",
    approvalModel:  raw.approvalModel  || "",
    approvalThreshold: raw.approvalThreshold || 1,
    createdAt:      raw.createdAt,
    fundedAt:       raw.fundedAt,
    description:    raw.contractDescription || "",
    terms:          raw.termsAndConditions   || "",
    milestones:     (raw.milestones || []).map((m) => ({
      milestoneId:             m.milestoneId,
      label:                   m.title || `Milestone ${m.sequenceNo}`,
      pct:                     m.releasePercentage || 0,
      amount:                  fmtAmt(m.releaseAmount),
      status:                  MILESTONE_STATUS_MAP[m.status] || "pending",
      triggerType:             m.releaseTriggerType || "",
      completionDate:          m.expectedCompletionDate || null,
      description:             m.deliverables || m.description || "",
      assignedApproverIndexes: Array.isArray(m.assignedApproverIndexes) ? m.assignedApproverIndexes : [],
    })),
    escrowDocuments: raw.escrowDocuments || [],
    conditions:     (raw.releaseConditions || []).map((rc) => ({
      conditionId:       rc.conditionId,
      sequenceNo:        rc.sequenceNo || 0,
      label:             rc.label || "",
      description:       rc.description || "",
      conditionType:     rc.conditionType || "",
      releaseTrigger:    rc.releaseTrigger || "",
      releasePercentage: rc.releasePercentage != null ? rc.releasePercentage : null,
      releaseAmount:     rc.releaseAmount != null ? rc.releaseAmount : 0,
      amountType:        rc.amountType || "",
      releaseDate:       rc.releaseDate || null,
      status:            (rc.status || "PENDING").toUpperCase(),
    })),
    approvalStatus: approvers.map((a) => ({
      role:   a.approverRole || "Approver",
      name:   a.name  || "",
      email:  a.email || "",
      status: ["APPROVED", "COMPLETED", "RELEASED"].includes((a.status || "").toUpperCase())
                ? "approved" : "pending",
    })),
    activityLog:    (raw.activityLogs || []).map((log) => ({
      eventType:      log.eventType      || "",
      eventTimestamp: log.eventTimestamp || null,
    })),
    transactionHash: null,
  };
}

export default function EscrowContractDetails() {
  const { contractId, userType } = useParams();
  const history = useHistory();

  const role = ["payer", "payee", "approval"].includes(userType) ? userType : "payer";

  const [activeTab, setActiveTab] = useState("overview");
  const [confirm, setConfirm] = useState(null);
  const [contractData, setContractData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approveLoadingId, setApproveLoadingId] = useState(null);
  const [rejectLoadingId, setRejectLoadingId] = useState(null);
  const [rejectModal, setRejectModal] = useState(null); // { milestoneId, trackingId }
  const [rejectComment, setRejectComment] = useState("");
  const [msDisputeModal, setMsDisputeModal] = useState(null); // { milestoneId, milestoneLabel }

  const myMerchantId = parseInt(localStorage.getItem(SITENAMEALIAS + "_merchant_id"), 10) || 0;
  const myEmail = (localStorage.getItem(SITENAMEALIAS + "_email") || "").toLowerCase();

  useEffect(() => {
    if (!contractId) return;
    const merchantId = parseInt(localStorage.getItem(SITENAMEALIAS + "_merchant_id"), 10);
    setLoading(true);
    fetchEscrowContractDetails(contractId, merchantId)
      .then((data) => {
        if (data.status === false) {
          showToast("error", data.message || "Failed to load contract details.");
          setLoading(false);
          return;
        }
        const raw = Array.isArray(data.data) ? data.data[0] : null;
        if (raw) {
          setContractData(mapApiContract(raw));
        }
        setLoading(false);
      })
      .catch(() => {
        showToast("error", "Failed to load contract details.");
        setLoading(false);
      });
  }, [contractId]);

  // ── Confirm ───────────────────────────────────────────────────
  const confirmConfig   = confirm ? CONFIRM_CONFIGS[confirm] || {}   : {};
  const confirmMessage  = confirm && contractData ? CONFIRM_MESSAGES[confirm]?.(contractData) || "" : "";

  const handleConfirm = () => {
    if (!contractData || !confirm) return;
    const now = new Date().toISOString();
    const update = (patch) => setContractData((prev) => ({ ...prev, ...patch }));

    if (confirm === "fund")             update({ status: "funded",    fundedAt: now });
    else if (confirm === "dispute")     update({ status: "in_dispute" });
    else if (confirm === "cancel")      update({ status: "cancelled", cancelledAt: now });
    else if (confirm === "approve_release") update({ status: "released", releasedAt: now });
    else if (confirm === "reject_release")  update({ status: "cancelled", cancelledAt: now, cancelReason: "Rejected by approver." });
    // request_approval / respond_dispute are informational in mock

    setConfirm(null);
  };

  // ── Milestone Approval ────────────────────────────────────────
  // trackingId is used for the loading spinner when milestoneId is null (condition-based approval)
  const handleMilestoneApprove = async (milestoneId, trackingId) => {
    if (!contractData) return;
    const loadId = trackingId !== undefined ? trackingId : milestoneId;
    setApproveLoadingId(loadId);
    try {
      const res = await fetch(`${WEBSERVICE}/escrow/process-escrow-approval`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + getAuthToken(),
          UUID: getAuthUuid(),
        },
        body: JSON.stringify({
          contractId: contractData.numericId,
          approverId: myMerchantId,
          status: "APPROVED",
          comment: "Approved after verification",
          milestoneId: milestoneId ?? null,
        }),
      });
      const data = await res.json();
      if (data.status === true || data.status === "true") {
        showToast("success", data.message || "Milestone approved successfully.");
        // Re-fetch contract to reflect updated milestone status
        const fresh = await fetchEscrowContractDetails(contractData.id, myMerchantId);
        if (fresh.status !== false) {
          const raw = Array.isArray(fresh.data) ? fresh.data[0] : null;
          if (raw) setContractData(mapApiContract(raw));
        }
      } else {
        showToast("error", data.message || "Failed to approve milestone.");
      }
    } catch {
      showToast("error", "Network error. Please try again.");
    } finally {
      setApproveLoadingId(null);
    }
  };

  // ── Milestone Rejection ───────────────────────────────────────
  const handleMilestoneReject = async (milestoneId, trackingId, comment) => {
    if (!contractData) return;
    const loadId = trackingId !== undefined ? trackingId : milestoneId;
    setRejectLoadingId(loadId);
    try {
      const res = await fetch(`${WEBSERVICE}/escrow/process-escrow-approval`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + getAuthToken(),
          UUID: getAuthUuid(),
        },
        body: JSON.stringify({
          contractId: contractData.numericId,
          approverId: myMerchantId,
          status: "REJECTED",
          comment: comment || "",
          milestoneId: milestoneId ?? null,
        }),
      });
      const data = await res.json();
      if (data.status === true || data.status === "true") {
        showToast("success", data.message || "Milestone rejected successfully.");
        const fresh = await fetchEscrowContractDetails(contractData.id, myMerchantId);
        if (fresh.status !== false) {
          const raw = Array.isArray(fresh.data) ? fresh.data[0] : null;
          if (raw) setContractData(mapApiContract(raw));
        }
      } else {
        showToast("error", data.message || "Failed to reject milestone.");
      }
    } catch {
      showToast("error", "Network error. Please try again.");
    } finally {
      setRejectLoadingId(null);
    }
  };

  // ── Milestone Dispute success — re-fetch contract ─────────────
  const handleMilestoneDisputeSuccess = async () => {
    try {
      const fresh = await fetchEscrowContractDetails(contractData.id, myMerchantId);
      if (fresh.status !== false) {
        const raw = Array.isArray(fresh.data) ? fresh.data[0] : null;
        if (raw) setContractData(mapApiContract(raw));
      }
    } catch { /* silent */ }
  };

  // ── Loading / Not found ───────────────────────────────────────
  if (loading || !contractData) {
    return (
      <Fragment>
        <style>{CSS}</style>
        <div className="container-scroller">
          <Sidebar /><Header />
          <div className="main-panel">
            <div className="ecd-page">
              <button className="ecd-back-link" onClick={() => history.goBack()}>
                <i className="fas fa-arrow-left" style={{ fontSize: 12 }} /> Back
              </button>
              <div className="ecd-not-found">
                {loading ? (
                  <>
                    <div className="ecd-not-found-icon"><i className="fas fa-spinner fa-spin" /></div>
                    <p className="ecd-not-found-title">Loading Contract…</p>
                  </>
                ) : (
                  <>
                    <div className="ecd-not-found-icon"><i className="fas fa-file-contract" /></div>
                    <p className="ecd-not-found-title">Contract Not Found</p>
                    <p className="ecd-not-found-sub">No contract found with ID <strong>{contractId}</strong></p>
                  </>
                )}
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </Fragment>
    );
  }

  const c = contractData;

  return (
    <Fragment>
      <style>{CSS}</style>
      <div className="container-scroller">
        <Sidebar /><Header />
        <div className="main-panel">
          <div className="ecd-page ecd-view-in">

            {/* Back */}
            <button className="ecd-back-link" onClick={() => history.goBack()}>
              <i className="fas fa-arrow-left" style={{ fontSize: 12 }} /> Back
            </button>

            {/* Page header */}
            <div className="ecd-hdr">
              <div className="ecd-hdr-left">
                <p className="ecd-id">{c.id}</p>
                <h1 className="ecd-title">{c.title}</h1>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
                  <StatusBadge status={c.status} />
                  {c.fundingStatus && <FundingStatusBadge status={c.fundingStatus} />}
                  <span className={`ecd-role-chip ${role}`}>
                    <i className={`fas ${ROLE_ICONS_FULL[role]}`} style={{ fontSize: 10 }} />
                    {ROLE_LABELS_FULL[role]}
                  </span>
                </div>
              </div>

              {/* Header actions */}
              <div className="ecd-hdr-actions">
                {c.status === "created" && (c.fundingStatus || "").toUpperCase() !== "FUNDED" && role === "payer" && (
                  <button className="ecd-btn ecd-btn-fund" onClick={() => history.push(`/escrow/contract/fund/${c.id}`)}>
                    <i className="fas fa-wallet" style={{ fontSize: 12 }} /> Fund Escrow
                  </button>
                )}
              </div>
            </div>

            {/* Tab bar */}
            <div className="ecd-tab-bar">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  className={`ecd-tab${activeTab === t.key ? " active" : ""}`}
                  onClick={() => setActiveTab(t.key)}
                >
                  <i className={`fas ${t.icon}`} style={{ fontSize: 11 }} />
                  {t.label}
                </button>
              ))}
            </div>

            {/* Tab panels */}
            {activeTab === "overview"     && <TabOverview      c={c} role={role} />}
            {activeTab === "participants" && <TabParticipants  c={c} role={role} />}
            {activeTab === "conditions"   && <TabConditions    c={c} role={role} myEmail={myEmail} myMerchantId={myMerchantId} approveLoadingId={approveLoadingId} onMilestoneApprove={handleMilestoneApprove} onMilestoneDispute={(milestoneId, milestoneLabel) => setMsDisputeModal({ milestoneId, milestoneLabel })} rejectLoadingId={rejectLoadingId} onMilestoneReject={(milestoneId, trackingId) => { setRejectModal({ milestoneId, trackingId }); setRejectComment(""); }} />}
            {activeTab === "distribution" && <TabDistribution  c={c} />}
            {activeTab === "activity"     && <TabActivityLog   c={c} />}

          </div>
          <Footer />
        </div>
      </div>

      <ConfirmDialog
        open={!!confirm}
        title={confirmConfig.title}
        message={confirmMessage}
        confirmLabel={confirmConfig.confirmLabel}
        danger={confirmConfig.danger}
        onConfirm={handleConfirm}
        onCancel={() => setConfirm(null)}
      />

      <RejectConfirmDialog
        open={!!rejectModal}
        comment={rejectComment}
        onCommentChange={setRejectComment}
        loading={!!rejectLoadingId}
        onConfirm={async () => {
          if (!rejectModal) return;
          await handleMilestoneReject(rejectModal.milestoneId, rejectModal.trackingId, rejectComment);
          setRejectModal(null);
          setRejectComment("");
        }}
        onCancel={() => { setRejectModal(null); setRejectComment(""); }}
      />

      <MilestoneDisputeModal
        open={!!msDisputeModal}
        contractNumericId={contractData?.numericId}
        milestoneId={msDisputeModal?.milestoneId ?? null}
        milestoneLabel={msDisputeModal?.milestoneLabel ?? ""}
        raisedBy={myMerchantId}
        onClose={() => setMsDisputeModal(null)}
        onSuccess={handleMilestoneDisputeSuccess}
      />
    </Fragment>
  );
}
